import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Globe, Menu, X, ChevronDown, LayoutDashboard, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import Logo from "./Logo";
const LANGUAGES = [
  { code: "en", label: "English", flag: "🇺🇸" },
  { code: "tr", label: "Türkçe", flag: "🇹🇷" },
  { code: "ar", label: "العربية", flag: "🇸🇦" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setMobileOpen(false); }, [location]);

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];
  const isActive = (href) => location.pathname === href;

  const navLinks = [
    { href: "/", label: t("nav.home") },
    { href: "/about", label: t("nav.about") },
    { href: "/treatments", label: t("nav.treatments") },
    { href: "/destinations", label: t("nav.destinations") },
    { href: "/how-it-works", label: t("nav.howItWorks") },
    { href: "/faq", label: t("nav.faq") },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "glass-nav" : "bg-transparent"}`}>
      <div className="max-w-[1200px] mx-auto px-6 h-20 flex items-center justify-between gap-8">
        <Link to="/" className="flex-shrink-0" data-testid="nav-logo" aria-label="Monimek Home">
          <Logo className="scale-[1.15]" />
        </Link>

        {/* Desktop Links */}
        <div className="hidden lg:flex items-center gap-7 flex-1 justify-center">
          {navLinks.map(link => (
            <Link key={link.href} to={link.href} data-testid={`nav-link-${link.href.replace("/", "") || "home"}`}
              className={`text-sm font-medium transition-colors relative ${isActive(link.href) ? "text-[#3FA7D6] nav-active" : "text-[#0B3C5D] hover:text-[#3FA7D6]"}`}>
              {link.label}
            </Link>
          ))}
        </div>

        {/* Right Controls */}
        <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1.5 text-sm font-medium text-[#0B3C5D] hover:text-[#3FA7D6] transition-colors px-3 py-2 rounded-full outline-none" data-testid="language-switcher">
              <Globe className="w-4 h-4" />
              <span>{currentLang.flag} {currentLang.code.toUpperCase()}</span>
              <ChevronDown className="w-3 h-3" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44 rounded-xl shadow-lg">
              {LANGUAGES.map(lang => (
                <DropdownMenuItem key={lang.code} onClick={() => i18n.changeLanguage(lang.code)}
                  className={`cursor-pointer rounded-lg ${i18n.language === lang.code ? "font-semibold text-[#3FA7D6]" : ""}`} data-testid={`lang-option-${lang.code}`}>
                  <span className="mr-2">{lang.flag}</span>{lang.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Auth-aware right controls */}
          {user ? (
            <Link to={user.role === "admin" ? "/admin" : user.role === "worker" ? "/worker" : "/dashboard"}
              data-testid="nav-dashboard-button"
              className="flex items-center gap-2 px-5 py-2.5 bg-[#0B3C5D] text-white text-sm font-semibold rounded-full hover:bg-[#3FA7D6] transition-all duration-200 hover:scale-105">
              {user.role === "admin" ? <ShieldCheck className="w-4 h-4" /> : <LayoutDashboard className="w-4 h-4" />}
              {user.role === "admin" ? "Admin Panel" : user.role === "worker" ? "My Portal" : "My Dashboard"}
            </Link>
          ) : (
            <>
              <Link to="/login" data-testid="nav-signin-button"
                className="px-4 py-2.5 text-[#0B3C5D] text-sm font-semibold rounded-full border-2 border-[#0B3C5D]/20 hover:border-[#3FA7D6] hover:text-[#3FA7D6] transition-all duration-200">
                Sign In
              </Link>
              <Link to="/contact" data-testid="nav-cta-button"
                className="px-5 py-2.5 bg-[#0B3C5D] text-white text-sm font-semibold rounded-full hover:bg-[#3FA7D6] transition-all duration-200 hover:scale-105 hover:shadow-[0_8px_20px_rgba(63,167,214,0.35)]">
                {t("nav.getConsultation")}
              </Link>
            </>
          )}
        </div>

        {/* Mobile Hamburger */}
        <button className="lg:hidden text-[#0B3C5D] p-2 rounded-lg hover:bg-gray-100 transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle" aria-label="Toggle menu">
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      <div className={`lg:hidden overflow-hidden transition-all duration-300 ${mobileOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"} bg-white border-t border-gray-100 shadow-xl`}>
        <div className="px-6 py-5 flex flex-col gap-1">
          {navLinks.map(link => (
            <Link key={link.href} to={link.href}
              className={`text-sm font-medium py-3 px-3 rounded-xl transition-colors ${isActive(link.href) ? "text-[#3FA7D6] bg-[#3FA7D6]/08" : "text-[#0B3C5D] hover:bg-gray-50"}`}>
              {link.label}
            </Link>
          ))}
          <div className="flex gap-2 flex-wrap pt-3 mt-2 border-t border-gray-100">
            {LANGUAGES.map(lang => (
              <button key={lang.code} onClick={() => i18n.changeLanguage(lang.code)}
                className={`px-3 py-1.5 text-xs rounded-full border transition-all ${i18n.language === lang.code ? "bg-[#0B3C5D] text-white border-[#0B3C5D]" : "border-gray-200 text-gray-600 hover:border-[#3FA7D6]"}`}>
                {lang.flag} {lang.code.toUpperCase()}
              </button>
            ))}
          </div>
          {user ? (
            <Link to={user.role === "admin" ? "/admin" : user.role === "worker" ? "/worker" : "/dashboard"}
              className="mt-3 text-center px-5 py-3.5 bg-[#0B3C5D] text-white text-sm font-semibold rounded-full">
              {user.role === "admin" ? "Admin Panel" : user.role === "worker" ? "My Portal" : "My Dashboard"}
            </Link>
          ) : (
            <div className="flex flex-col gap-2 mt-3">
              <Link to="/login" data-testid="mobile-signin-button"
                className="text-center px-5 py-3 border-2 border-[#0B3C5D] text-[#0B3C5D] text-sm font-semibold rounded-full hover:bg-[#0B3C5D] hover:text-white transition-colors">
                Sign In
              </Link>
              <Link to="/contact"
                className="text-center px-5 py-3.5 bg-[#0B3C5D] text-white text-sm font-semibold rounded-full">
                {t("nav.getConsultation")}
              </Link>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
